// Sprint-16 — in-app notification HTTP wrapper.
//
// Thin axios shim over /api/v1/me/notifications/*. The store
// (notificationsStore.ts) owns optimistic state + the 60s poll; this file
// is just transport. All calls flow through the shared `api` instance so
// bearer-token auth + 401 redirect work automatically.

import { api } from './apiClient';
import type { Notification } from '../types/notification';

export interface ListOpts {
  /** ISO timestamp — only return rows created strictly after this. */
  since?: string;
  /** Page size — server clamps to [1, 100], default 50. */
  limit?: number;
}

export const notificationsApi = {
  /**
   * Fetch the inbox. Returns newest first. Use `since` for incremental
   * polling — pass the timestamp of the newest row you have and the server
   * only returns what's newer.
   */
  async list(opts: ListOpts = {}): Promise<Notification[]> {
    const r = await api.get('/api/v1/me/notifications', { params: opts });
    return (r.data?.notifications || []) as Notification[];
  },

  /** Single integer for the bell badge. Cheap — partial index on the server. */
  async unreadCount(): Promise<number> {
    const r = await api.get('/api/v1/me/notifications/unread-count');
    return Number(r.data?.count) || 0;
  },

  /**
   * Flip a single row to read. Idempotent on the server — flipping an
   * already-read row is a no-op. Returns the updated row's minimal shape.
   */
  async markRead(id: string): Promise<{ id: string; read_at: string | null }> {
    const r = await api.post(`/api/v1/me/notifications/${id}/read`);
    return r.data?.notification;
  },

  /** Mark every unread row read. Returns the number updated. */
  async markAllRead(): Promise<number> {
    const r = await api.post('/api/v1/me/notifications/read-all');
    return Number(r.data?.updated) || 0;
  },
};
